/**
 * HTTP-only Signature Confirmation
 * 
 * Confirms a Solana transaction signature using HTTP polling only.
 * Does NOT rely on WebSocket subscriptions, which can be flaky.
 * 
 * Used by:
 * - Fee claim flows (creator and partner)
 * - Token creation flows (via transactionHelper.ts)
 */

import { Connection, Commitment, SignatureStatus } from '@solana/web3.js';
import * as Sentry from '@sentry/react';
import { captureError } from '@/lib/sentry';
import { debug } from "@/lib/debug";

export type ConfirmationStage = 'submitted' | 'confirming' | 'confirmed' | 'failed' | 'pending';

export interface ConfirmationProgress {
  stage: ConfirmationStage;
  signature: string;
  pollCount: number;
  elapsedMs: number;
  confirmations: number | null;
  error: string | null;
}

export interface ConfirmSignatureOptions {
  /** Commitment level for confirmation */
  commitment?: Commitment;
  /** Polling interval in ms (fixed, ignored if pollIntervalFn is provided) */
  pollIntervalMs?: number;
  /** Dynamic polling interval based on poll count. Overrides pollIntervalMs. */
  pollIntervalFn?: (pollCount: number) => number;
  /** Total timeout in ms */
  timeoutMs?: number;
  /** Progress callback */
  onProgress?: (progress: ConfirmationProgress) => void;
  /** Label for logging */
  label?: string;
}

export interface ConfirmSignatureResult {
  /** Whether the transaction is confirmed */
  confirmed: boolean;
  /** Whether the transaction is still pending (not confirmed but no error) */
  pending: boolean;
  /** Error message if failed */
  error: string | null;
  /** Number of confirmations if available */
  confirmations: number | null;
  /** Time taken in ms */
  elapsedMs: number;
}

const DEFAULT_OPTIONS = {
  commitment: 'confirmed' as Commitment,
  pollIntervalMs: 1500,
  timeoutMs: 120000, // 2 minutes
};

/**
 * Confirm a transaction signature using HTTP polling only.
 * 
 * This function:
 * 1. Polls getSignatureStatuses over HTTP
 * 2. Checks for confirmed/finalized status
 * 3. Detects on-chain errors
 * 4. Treats timeout as "pending" not "failed"
 * 5. Never uses WebSocket subscriptions
 */
export async function confirmSignatureHttp(
  connection: Connection,
  signature: string,
  options: ConfirmSignatureOptions = {}
): Promise<ConfirmSignatureResult> {
  const {
    commitment,
    pollIntervalMs,
    timeoutMs,
  } = { ...DEFAULT_OPTIONS, ...options };
  const { onProgress, label = 'confirm' } = options;

  const startTime = Date.now();
  let pollCount = 0;

  const updateProgress = (stage: ConfirmationStage, error: string | null = null, confirmations: number | null = null) => {
    onProgress?.({
      stage,
      signature,
      pollCount,
      elapsedMs: Date.now() - startTime,
      confirmations,
      error,
    });
  };

  const addBreadcrumb = (message: string, data?: Record<string, unknown>) => {
    Sentry.addBreadcrumb({
      category: 'transaction',
      message: `[${label}] ${message}`,
      level: 'info',
      data: { signature, pollCount, ...data },
    });
    debug.trace(`[ConfirmHttp:${label}] ${message}`, { signature: signature.slice(0, 8) + '...', ...data });
  };

  addBreadcrumb('Starting HTTP confirmation polling');
  updateProgress('confirming');

  while (Date.now() - startTime < timeoutMs) {
    pollCount++;

    try {
      // Poll signature status via HTTP
      const statusResponse = await connection.getSignatureStatuses(
        [signature],
        { searchTransactionHistory: true }
      );
      const status: SignatureStatus | null = statusResponse?.value?.[0] ?? null;

      if (status) {
        // Check for on-chain error
        if (status.err) {
          const errorStr = typeof status.err === 'string' 
            ? status.err 
            : JSON.stringify(status.err);
          addBreadcrumb('Transaction failed on-chain', { error: errorStr });
          updateProgress('failed', errorStr);
          return {
            confirmed: false,
            pending: false,
            error: `Transaction failed: ${errorStr}`,
            confirmations: null,
            elapsedMs: Date.now() - startTime,
          };
        }

        // Check confirmation status
        const confirmedStatus = status.confirmationStatus;
        const confirmations = status.confirmations ?? 0;

        if (
          confirmedStatus === 'confirmed' ||
          confirmedStatus === 'finalized' ||
          (commitment === 'confirmed' && confirmations > 0)
        ) {
          addBreadcrumb('Transaction confirmed', { confirmations, status: confirmedStatus });
          updateProgress('confirmed', null, confirmations);
          return {
            confirmed: true,
            pending: false,
            error: null,
            confirmations,
            elapsedMs: Date.now() - startTime,
          };
        }

        // Still processing - update progress
        updateProgress('confirming', null, confirmations);
      }

      // Log progress periodically
      if (pollCount % 10 === 0) {
        addBreadcrumb('Still polling', { 
          pollCount,
          elapsedMs: Date.now() - startTime,
        });
      }

    } catch (err) {
      // Network error during polling - continue trying
      const errMsg = err instanceof Error ? err.message : 'Unknown error';
      console.warn(`[ConfirmHttp:${label}] Poll error (will retry):`, errMsg);
      captureError(err instanceof Error ? err : new Error(errMsg), {
        feature: 'confirm_signature_polling',
        signature: signature?.slice(0, 8),
        pollCount,
      });
    }

    // Wait before next poll (use dynamic interval if provided)
    const interval = options.pollIntervalFn
      ? options.pollIntervalFn(pollCount)
      : pollIntervalMs;
    await sleep(interval);
  }

  // Timeout reached - treat as PENDING not FAILED
  // The transaction may still confirm later
  addBreadcrumb('Confirmation timeout - treating as pending', {
    pollCount,
    timeoutMs,
    elapsedMs: Date.now() - startTime,
  });
  updateProgress('pending');

  Sentry.captureMessage(`Transaction confirmation timeout after ${timeoutMs}ms`, 'warning');

  return {
    confirmed: false,
    pending: true,
    error: null,
    confirmations: null,
    elapsedMs: Date.now() - startTime,
  };
}

/**
 * Handle TransactionExpiredTimeoutError by running HTTP confirmation
 */
export async function handleExpiredTimeoutError(
  connection: Connection,
  signature: string | null,
  originalError: Error,
  options: ConfirmSignatureOptions = {}
): Promise<ConfirmSignatureResult> {
  const { label = 'recovery' } = options;

  // If we don't have a signature, we can't recover
  if (!signature) {
    return {
      confirmed: false,
      pending: false,
      error: originalError.message,
      confirmations: null,
      elapsedMs: 0,
    };
  }

  debug.trace(`[ConfirmHttp:${label}] Handling timeout error, checking signature via HTTP...`);
  Sentry.addBreadcrumb({
    category: 'transaction',
    message: `[${label}] Recovering from timeout error`,
    level: 'warning',
    data: { signature, originalError: originalError.message },
  });

  // Run HTTP confirmation to check actual status
  return confirmSignatureHttp(connection, signature, {
    ...options,
    timeoutMs: 30000, // Shorter timeout for recovery check
  });
}

/**
 * Check if an error is a TransactionExpiredTimeoutError
 */
export function isTransactionExpiredTimeoutError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('transaction was not confirmed') ||
      message.includes('transactionexpiredtimeouterror') ||
      message.includes('block height exceeded') ||
      message.includes('transaction expired')
    );
  }
  return false;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Aggressive polling schedule for trade confirmations.
 * Most Jito-routed transactions land within 400ms.
 */
export const tradingPollInterval = (pollCount: number): number => {
  if (pollCount <= 5) return 200;   // 0-1s: catch fast Jito landings
  if (pollCount <= 10) return 500;  // 1-3.5s: moderate pace
  return 1500;                       // 3.5s+: standard pace
};
